import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';

import { getWidget } from '../interactives/widgetRegistry';

import VideoElementContent from './VideoElementContent';

import {
  DEFAULT_SLIDE_BODY_FONT_SIZE_PT,
  DEFAULT_SLIDE_BODY_LINE_HEIGHT,
  DEFAULT_SLIDE_TEXT_PADDING_PX,
} from '@/shared/types/slide';
import type {
  SlideElement as SlideElementType,
  TextElement,
  ShapeElement,
  ImageElement,
  InteractiveElement,
  PlaceholderId,
  SlideTextStyle,
} from '@/shared/types/slide';
import type { ResolvedSlideElement } from '@/shared/theming/resolve';
import { resolveSignedUrl, type StorageBucket } from '@/shared/services/storageService';

/** Display-only hint shown for an empty placeholder-bound text element (§8.3).
 *  Never persisted — the stored `content.text` stays `''`. */
const PLACEHOLDER_HINTS: Record<PlaceholderId, string> = {
  TITLE: 'Click to add title',
  SUBTITLE: 'Click to add subtitle',
  BODY: 'Click to add text',
  LEFT_BODY: 'Click to add text',
  RIGHT_BODY: 'Click to add text',
};

// ---------------------------------------------------------------------------
// SlideElement
//
// Renders a single slide element (text / shape / image) at its absolute
// position inside the canvas.  Text elements use a <textarea> for plain-text
// editing on double-click.  Inline rich text can be added later via TipTap on
// the `tiptapDoc` content field.
// ---------------------------------------------------------------------------

interface SlideElementProps {
  element: ResolvedSlideElement;
  isSelected: boolean;
  zoom: number;
  canEdit?: boolean;
  /** True only inside <PresentationOverlay>. Distinct from `!canEdit`, which
   *  is also true for read-only deck previews. */
  isPresenting?: boolean;
  suppressVideoPreview?: boolean;
  videoPreviewResetKey?: number;
  onSelect: (id: string) => void;
  onDoubleClick?: (id: string) => void;
  onTextChange?: (id: string, text: string) => void;
  onUpdateElement?: (id: string, updates: Partial<SlideElementType>) => void;
}

const SlideElement: React.FC<SlideElementProps> = ({
  element,
  isSelected,
  zoom: _zoom,
  canEdit = true,
  isPresenting = false,
  suppressVideoPreview = false,
  videoPreviewResetKey,
  onSelect,
  onDoubleClick,
  onTextChange,
  onUpdateElement,
}) => {
  const { position } = element;
  const [isEditing, setIsEditing] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (canEdit) onSelect(element.id);
    },
    [element.id, onSelect, canEdit]
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (element.type === 'text' && canEdit) {
        setIsEditing(true);
      }
      onDoubleClick?.(element.id);
    },
    [element.id, element.type, canEdit, onDoubleClick]
  );

  const handleVideoPreviewStart = useCallback(() => {
    if (canEdit) onSelect(element.id);
  }, [canEdit, element.id, onSelect]);

  const handleBlur = useCallback(() => {
    if (element.type === 'text' && isEditing) {
      setIsEditing(false);
      const text = textRef.current?.value ?? '';
      onTextChange?.(element.id, text);
    }
  }, [element.id, element.type, isEditing, onTextChange]);

  const containerStyle: React.CSSProperties = useMemo(
    () => ({
      position: 'absolute',
      left: position.x,
      top: position.y,
      width: position.width,
      height: position.height,
      // Rotation about the box center (default transform-origin), with flips
      // applied first so the composed matrix is rotate · flip — matching how
      // the import decomposes Google's affine transform.
      transform:
        [
          position.rotation ? `rotate(${position.rotation}deg)` : '',
          position.flipH ? 'scaleX(-1)' : '',
          position.flipV ? 'scaleY(-1)' : '',
        ]
          .filter(Boolean)
          .join(' ') || undefined,
      zIndex: position.zIndex,
      cursor: 'default',
      // outline: isSelected ? '2px solid #1976d2' : 'none',
      outlineOffset: 1,
      boxSizing: 'border-box' as const,
      // Prevent the element from interfering with Moveable ghost
      pointerEvents: 'auto' as const,
    }),
    [position, isSelected]
  );

  // Accessibility label based on element type
  const ariaLabel = useMemo(() => {
    switch (element.type) {
      case 'text':
        return `Text element: ${element.content.text.slice(0, 40)}`;
      case 'shape':
        return `${element.content.shapeType} shape`;
      case 'image':
        return element.content.alt || element.content.imageQuery || 'Image element';
      case 'interactive': {
        const entry = getWidget(element.content.widgetType);
        return `Interactive: ${entry?.name ?? element.content.widgetType}`;
      }
      case 'video':
        return element.content.alt || element.content.title || 'Video element';
    }
  }, [element]);

  return (
    <div
      className="slide-element"
      data-element-id={element.id}
      style={containerStyle}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {element.type === 'text' && (
        <TextElementContent
          element={element}
          isEditing={isEditing}
          textRef={textRef}
          onBlur={handleBlur}
        />
      )}
      {element.type === 'shape' && <ShapeElementContent element={element} />}
      {element.type === 'image' && <ImageElementContent element={element} />}
      {element.type === 'interactive' && (
        <InteractiveElementContent
          element={element}
          canEdit={canEdit}
          onUpdateElement={onUpdateElement}
        />
      )}
      {element.type === 'video' && (
        <VideoElementContent
          element={element}
          isPresenting={isPresenting}
          suppressPreview={suppressVideoPreview}
          previewResetKey={videoPreviewResetKey}
          onPreviewStart={handleVideoPreviewStart}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

/** Shared CSS properties for both read and edit modes. */
export function textStyleToCss(style: SlideTextStyle | undefined): React.CSSProperties {
  const s = style ?? {};
  return {
    width: '100%',
    height: '100%',
    fontFamily: s.fontFamily ?? 'Arial, sans-serif',
    fontSize: s.fontSize != null ? `${s.fontSize}pt` : `${DEFAULT_SLIDE_BODY_FONT_SIZE_PT}pt`,
    fontWeight: s.fontWeight ?? 'normal',
    fontStyle: s.fontStyle ?? 'normal',
    color: s.color ?? '#000',
    textAlign: s.textAlign ?? 'left',
    lineHeight: s.lineHeight ?? DEFAULT_SLIDE_BODY_LINE_HEIGHT,
    backgroundColor: s.backgroundColor ?? 'transparent',
    borderColor: s.borderColor ?? 'transparent',
    borderWidth: s.borderWidth ?? 0,
    borderStyle: s.borderStyle ?? 'none',
    borderRadius: s.borderRadius ?? 0,
    padding: s.padding ?? DEFAULT_SLIDE_TEXT_PADDING_PX,
    textDecoration: s.textDecoration ?? 'none',
    overflow: 'hidden',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    boxSizing: 'border-box',
  };
}

const TextElementContent: React.FC<{
  element: TextElement;
  isEditing: boolean;
  textRef: React.RefObject<HTMLTextAreaElement | null>;
  onBlur: () => void;
}> = ({ element, isEditing, textRef, onBlur }) => {
  const { style, content } = element;
  const baseCss = textStyleToCss(style);
  const hint =
    content.text === '' && element.placeholderId ? PLACEHOLDER_HINTS[element.placeholderId] : null;

  // Auto-focus the textarea when entering edit mode
  useEffect(() => {
    if (isEditing && textRef.current) {
      textRef.current.focus();
      // Place cursor at end of text
      const len = textRef.current.value.length;
      textRef.current.setSelectionRange(len, len);
    }
  }, [isEditing, textRef]);

  if (isEditing) {
    return (
      <textarea
        ref={textRef}
        defaultValue={content.text}
        onBlur={onBlur}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            // Commit and exit — blur triggers the onBlur handler
            textRef.current?.blur();
            return;
          }
          // Stop propagation so canvas-level handlers (arrow nudge, delete, etc.)
          // don't fire while typing in the textarea
          e.stopPropagation();
        }}
        style={{
          ...baseCss,
          cursor: 'text',
          outline: 'none',
          resize: 'none',
          border: 'none',
        }}
      />
    );
  }

  // Vertical alignment within the box (Google `contentAlignment`). Only switch
  // to a flex column for middle/bottom so top-aligned text keeps its exact
  // current block layout; the inner wrapper preserves horizontal textAlign.
  const verticalAlign = style?.verticalAlign;
  const justifyContent =
    verticalAlign === 'middle' ? 'center' : verticalAlign === 'bottom' ? 'flex-end' : undefined;

  const body = hint ?? content.text;
  return (
    <div
      style={{
        ...baseCss,
        cursor: 'default',
        outline: 'none',
        userSelect: 'none',
        ...(justifyContent ? { display: 'flex', flexDirection: 'column', justifyContent } : {}),
        ...(hint ? { color: '#9e9e9e' } : {}),
      }}
    >
      {justifyContent ? <div style={{ width: '100%' }}>{body}</div> : body}
    </div>
  );
};

const ShapeElementContent: React.FC<{ element: ShapeElement }> = ({ element }) => {
  const { content } = element;
  const { shapeType, fillColor = '#3498db', borderRadius = 0 } = content;
  const isLine = shapeType === 'line';
  const strokeColor = content.strokeColor ?? (isLine ? '#2980b9' : 'transparent');
  const strokeWidth = content.strokeWidth ?? (isLine ? 2 : 0);

  if (isLine) {
    return (
      <svg width="100%" height="100%" style={{ display: 'block' }}>
        <line x1="0" y1="50%" x2="100%" y2="50%" stroke={strokeColor} strokeWidth={strokeWidth} />
      </svg>
    );
  }

  if (shapeType === 'circle') {
    return (
      <svg width="100%" height="100%" style={{ display: 'block' }}>
        <ellipse
          cx="50%"
          cy="50%"
          rx="49%"
          ry="49%"
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
      </svg>
    );
  }

  // rectangle (default). `roundedCorners` restricts the radius to specific
  // corners (e.g. an imported single-rounded-corner rectangle); absent rounds
  // all four.
  const corners = content.roundedCorners;
  const cornerRadius = (corner: 'tl' | 'tr' | 'br' | 'bl') =>
    !corners || corners.includes(corner) ? borderRadius : 0;
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: fillColor,
        border: `${strokeWidth}px solid ${strokeColor}`,
        borderTopLeftRadius: cornerRadius('tl'),
        borderTopRightRadius: cornerRadius('tr'),
        borderBottomRightRadius: cornerRadius('br'),
        borderBottomLeftRadius: cornerRadius('bl'),
        boxSizing: 'border-box',
      }}
    />
  );
};

const ImageElementContent: React.FC<{ element: ImageElement }> = ({ element }) => {
  const { content } = element;
  const [hasError, setHasError] = useState(false);
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  // Reset error state when the image URL changes (e.g. after editing via dialog)
  useEffect(() => {
    setHasError(false);
  }, [content.src]);

  // Resolve storage signed URL
  useEffect(() => {
    if (!content.storagePath || !content.storageBucket) {
      setResolvedSrc(null);
      return;
    }
    // If src is already set (transient signed URL from upload), use it directly
    if (content.src) {
      setResolvedSrc(content.src);
      return;
    }
    let cancelled = false;
    setIsResolving(true);
    resolveSignedUrl(content.storageBucket as StorageBucket, content.storagePath)
      .then((url) => {
        if (!cancelled) setResolvedSrc(url);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [content.storageBucket, content.storagePath, content.src]);

  const displaySrc = resolvedSrc || content.src;

  if (!displaySrc && !isResolving) {
    // Placeholder for unresolved images
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '2px dashed #ccc',
          borderRadius: 1,
          color: '#999',
          fontSize: 13,
          gap: 0.5,
          cursor: content.imageQuery ? 'pointer' : 'default',
          transition: 'border-color 0.15s',
          '&:hover': content.imageQuery ? { borderColor: '#90caf9' } : undefined,
        }}
      >
        <ImageOutlinedIcon sx={{ fontSize: 36, color: '#bbb' }} />
        {content.imageQuery && <span>{content.imageQuery}</span>}
        {content.imageQuery && (
          <span style={{ fontSize: 11, color: '#aaa' }}>Double-click to find image</span>
        )}
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fef2f2',
          border: '2px dashed #e57373',
          borderRadius: 1,
          color: '#c62828',
          fontSize: 11,
          gap: 0.5,
          p: 1,
          overflow: 'hidden',
          wordBreak: 'break-all',
        }}
      >
        <ImageOutlinedIcon sx={{ fontSize: 28, color: '#e57373' }} />
        <span>Failed to load</span>
        <span
          style={{ opacity: 0.7, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {displaySrc || content.storagePath}
        </span>
      </Box>
    );
  }

  if (isResolving) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: '#f0f0f0',
          borderRadius: 1,
          animation: 'slide-image-shimmer 1.5s infinite',
          '@keyframes slide-image-shimmer': {
            '0%': { opacity: 0.6 },
            '50%': { opacity: 1 },
            '100%': { opacity: 0.6 },
          },
        }}
      />
    );
  }

  // Cropped images (e.g. from Google Slides import) point `src` at the full,
  // uncropped source. We reproduce the crop window by oversizing the img inside
  // an overflow-hidden box and offsetting it, so only the intended region shows.
  // The visible window's aspect ratio matches the element box, so it fills it.
  if (content.crop) {
    const { left, right, top, bottom } = content.crop;
    const visibleW = Math.max(1 - left - right, 0.001);
    const visibleH = Math.max(1 - top - bottom, 0.001);
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <img
          src={displaySrc}
          alt={content.alt ?? ''}
          onError={() => setHasError(true)}
          style={{
            position: 'absolute',
            width: `${(100 / visibleW).toFixed(4)}%`,
            height: `${(100 / visibleH).toFixed(4)}%`,
            left: `${(-(left / visibleW) * 100).toFixed(4)}%`,
            top: `${(-(top / visibleH) * 100).toFixed(4)}%`,
            objectFit: 'fill',
            display: 'block',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
          draggable={false}
        />
      </div>
    );
  }

  return (
    <img
      src={displaySrc}
      alt={content.alt ?? ''}
      onError={() => setHasError(true)}
      style={{
        width: '100%',
        height: '100%',
        objectFit: content.objectFit ?? 'cover',
        display: 'block',
        pointerEvents: 'none',
        userSelect: 'none',
      }}
      draggable={false}
    />
  );
};

const InteractiveElementContent: React.FC<{
  element: Extract<ResolvedSlideElement, { type: 'interactive' }>;
  canEdit: boolean;
  onUpdateElement?: (id: string, updates: Partial<SlideElementType>) => void;
}> = ({ element, canEdit, onUpdateElement }) => {
  const { content, position } = element;
  const entry = getWidget(content.widgetType);

  const handleConfigChange = useCallback(
    (patch: Record<string, unknown>) => {
      onUpdateElement?.(element.id, {
        content: { ...content, config: { ...content.config, ...patch } },
      } as Partial<InteractiveElement>);
    },
    [onUpdateElement, element.id, content]
  );

  if (!entry) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '2px dashed #ccc',
          borderRadius: 1,
          color: '#999',
          fontSize: 13,
          gap: 0.5,
        }}
      >
        <WidgetsOutlinedIcon sx={{ fontSize: 36, color: '#bbb' }} />
        <span>Unsupported widget: {content.widgetType}</span>
      </Box>
    );
  }

  const WidgetComponent = entry.component;

  return (
    <Box data-interactive-widget sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <WidgetComponent
        config={content.config}
        interactive={!canEdit}
        width={position.width}
        height={position.height}
        onConfigChange={handleConfigChange}
      />
      {/* Edit-mode overlay badge */}
      {canEdit && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            left: 4,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: '#fff',
            fontSize: 10,
            px: 0.75,
            py: 0.25,
            borderRadius: 0.5,
            pointerEvents: 'none',
            lineHeight: 1.2,
          }}
        >
          {entry.name}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(SlideElement);
